import { CoordinatesSection } from '@/components/attractions/CoordinatesSection';
import { HealthCheckList } from '@/components/health/HealthCheckList';
import { HealthRowActions } from '@/components/health/HealthRowActions';
import { useHealthCheck } from '@/hooks/useHealthCheck';
import { useAttractionContext } from '@/pages/attractions/useAttractionContext';
import { Loading } from '@startupquickstart/frontend';
import {
  AudioLines,
  CheckCircle2,
  Languages,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Attraction-scoped Health view. A single attraction has too narrow a
 * scope for sub-tabs, so the three checks (Location, Descriptions &
 * Voiceovers, Translations) are stacked on one scrollable page.
 */
export function AttractionHealthTab(): JSX.Element | null {
  const { id } = useParams<{ id: string }>();
  const { attraction, isLoading: attractionLoading } = useAttractionContext();

  const content = useHealthCheck('attraction', 'content', id);
  const translations = useHealthCheck('attraction', 'translations', id);

  if (!id) return null;

  return (
    <div className="space-y-6 px-4 pb-4">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">Attraction health</h2>
        <p className="text-sm text-muted-foreground">
          Quality checks scoped to this attraction's location, content and translations.
        </p>
      </header>

      <Section title="Location" Icon={MapPin}>
        {attractionLoading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : attraction?.isAddressVerified ? (
          <AllClearCard
            title="Location verified"
            description="This attraction's coordinates have been externally verified."
          />
        ) : attraction ? (
          <CoordinatesSection attraction={attraction} layout="wide" />
        ) : null}
      </Section>

      <Section title="Descriptions & Voiceovers" Icon={AudioLines}>
        <HealthCheckList
          data={content.data?.data}
          isLoading={content.isLoading}
          IconAllClear={AudioLines}
          allClearTitle="Descriptions and voiceovers ready"
          allClearDescription="Every primary, theme, and translation has a description and an in-sync voiceover."
          emptyTitle="Nothing to check"
          emptyDescription="No content has been added yet."
          renderRowActions={(row) => <HealthRowActions row={row} />}
        />
      </Section>

      <Section title="Translations" Icon={Languages}>
        <HealthCheckList
          data={translations.data?.data}
          isLoading={translations.isLoading}
          IconAllClear={Languages}
          allClearTitle="Every translation present"
          allClearDescription="Every known language has a translation for this attraction."
          emptyTitle="No translations to check"
          emptyDescription="Once at least one translation exists, gaps for the rest will surface here."
          renderRowActions={(row) => <HealthRowActions row={row} />}
        />
      </Section>
    </div>
  );
}

function Section({
  title,
  Icon,
  children,
}: {
  title: string;
  Icon: LucideIcon;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
        <Icon className="h-4 w-4 text-zinc-500" />
        {title}
      </h3>
      <div>{children}</div>
    </section>
  );
}

function AllClearCard({
  title,
  description,
}: {
  title: string;
  description: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
      <CheckCircle2 className="h-5 w-5" />
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs">{description}</p>
      </div>
    </div>
  );
}
